'use client'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { StaffEventWithWorker, StaffEventType, AppointmentWithDetails, AppointmentStatus, JobcardAppointment } from '@garage/shared'
import { STAFF_EVENT_TYPE_LABELS, APPOINTMENT_STATUS_LABELS } from '@garage/shared'
import { Dialog, Button, StatusDot, CarIcon, useSnackbar } from '@/ui'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFeatures } from '@/lib/hooks.js'
import { JOBCARD_STATUS_TONE } from './StatusChip.js'

const EVENT_TONE: Record<StaffEventType, 'warning' | 'error' | 'success' | 'info' | 'teal'> = {
  late: 'warning',
  absent: 'error',
  holiday: 'success',
  other: 'info',
  overtime: 'teal',
}

function formatTime(appointmentAt: string): string {
  const time = appointmentAt.slice(11, 16) // "HH:mm"
  if (!time) return ''
  const [h, min] = time.split(':').map(Number)
  const period = h! >= 12 ? 'PM' : 'AM'
  const hour12 = h! % 12 === 0 ? 12 : h! % 12
  return `${hour12}:${String(min).padStart(2, '0')} ${period}`
}

interface DayDetailDialogProps {
  open: boolean
  /** "YYYY-MM-DD" */
  dateKey: string | null
  bookedCars: JobcardAppointment[]
  customerAppointments: AppointmentWithDetails[]
  events: StaffEventWithWorker[]
  onClose: () => void
  onAddEvent: () => void
  onAddAppointment: () => void
  onSelectAppointment: (appointment: JobcardAppointment) => void
  onSelectAppointmentForEdit: (appointment: AppointmentWithDetails) => void
  onSelectEvent: (event: StaffEventWithWorker) => void
  onCreateJobcard: (appointment: AppointmentWithDetails) => void
}

/** Western (Latin) numerals in both languages — matches the rest of the app's numeral convention. */
function formatDateHeading(dateKey: string, language: string): string {
  const [y, m, d] = dateKey.split('-').map(Number)
  const locale = language === 'ar' ? 'ar-u-nu-latn' : 'en-US'
  return new Date(y!, m! - 1, d!).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Everything registered on one day — every car booked in, every staff event —
 * opened by clicking the day itself. Clicking within it goes straight to the
 * jobcard or the event's own edit dialog; "Add event" starts a new one dated
 * to this day.
 */
export function DayDetailDialog({
  open,
  dateKey,
  bookedCars,
  customerAppointments,
  events,
  onClose,
  onAddEvent,
  onAddAppointment,
  onSelectAppointment,
  onSelectAppointmentForEdit,
  onSelectEvent,
  onCreateJobcard,
}: DayDetailDialogProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { appointmentsEnabled } = useFeatures()

  const setStatusMutation = useMutation({
    mutationFn: (input: { appointmentId: string; status: AppointmentStatus }) =>
      api.appointments.setStatus(input.appointmentId, { status: input.status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
    onError: (error) => {
      snackbar.error(error instanceof Error ? error.message : t('common.errorOccurred'))
    },
  })

  const isEmpty = bookedCars.length === 0 && (appointmentsEnabled ? customerAppointments.length === 0 : true) && events.length === 0
  const APPOINTMENT_TONE: Record<AppointmentStatus, 'violet' | 'success' | 'error' | 'neutral'> = {
    scheduled: 'violet',
    arrived: 'success',
    no_show: 'error',
    cancelled: 'neutral',
  }

  if (!dateKey) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={formatDateHeading(dateKey, i18n.language)}
      size="md"
      actions={
        <div className="flex gap-3 justify-end w-full">
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          {appointmentsEnabled && (
            <Button variant="outlined" onClick={onAddAppointment}>
              {t('appointments.addAppointment')}
            </Button>
          )}
          <Button variant="filled" onClick={onAddEvent}>
            {t('calendar.addEvent')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {isEmpty && (
          <p className="text-body-md text-on-surface-variant">
            {t('calendar.nothingRegistered')}
          </p>
        )}

        {appointmentsEnabled && customerAppointments.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-label-lg text-on-surface-variant">{t('appointments.title')}</h3>
            <div className="space-y-2">
              {/* Show non-cancelled appointments first, sorted by time */}
              {customerAppointments
                .filter((a) => a.status !== 'cancelled')
                .map((appointment) => (
                  <div
                    key={appointment.id}
                    className="rounded-md border border-outline-variant bg-surface-high p-3 space-y-2"
                  >
                    {/* Header line: time · customer · phone · car · reason · notes */}
                    <div className="text-body-sm text-on-surface">
                      {appointment.time ? formatTime(`2000-01-01T${appointment.time}:00`) : t('appointments.anyTime')}
                      {' · '}
                      {appointment.customerLabel}
                      {appointment.phoneLabel && (
                        <>
                          {' · '}
                          <a href={`tel:${appointment.phoneLabel}`} className="text-primary interactive">
                            {appointment.phoneLabel}
                          </a>
                        </>
                      )}
                      {appointment.carLabel && (
                        <>
                          {' · '}
                          {appointment.carLabel}
                        </>
                      )}
                      {appointment.reason && (
                        <>
                          {' · '}
                          {appointment.reason}
                        </>
                      )}
                      {appointment.notes && (
                        <>
                          {' · '}
                          {appointment.notes}
                        </>
                      )}
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center gap-2">
                      <StatusDot
                        tone={APPOINTMENT_TONE[appointment.status]}
                        label={t(`appointments.status.${appointment.status}`, APPOINTMENT_STATUS_LABELS[appointment.status])}
                      />
                    </div>

                    {/* Actions row */}
                    <div className="flex gap-2 flex-wrap pt-1">
                      {appointment.status === 'scheduled' && (
                        <>
                          <Button
                            variant="filled"
                            size="sm"
                            onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'arrived' })}
                            disabled={setStatusMutation.isPending}
                          >
                            {t('appointments.actions.arrived')}
                          </Button>
                          <Button
                            variant="filled"
                            size="sm"
                            onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'no_show' })}
                            disabled={setStatusMutation.isPending}
                          >
                            {t('appointments.actions.didntCome')}
                          </Button>
                          <Button
                            variant="filled"
                            size="sm"
                            onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'cancelled' })}
                            disabled={setStatusMutation.isPending}
                          >
                            {t('appointments.actions.cancel')}
                          </Button>
                        </>
                      )}
                      {appointment.status !== 'scheduled' && (
                        <Button
                          variant="filled"
                          size="sm"
                          onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'scheduled' })}
                          disabled={setStatusMutation.isPending}
                        >
                          {t('appointments.actions.backToExpected')}
                        </Button>
                      )}

                      <Button
                        variant="filled"
                        size="sm"
                        onClick={() => onSelectAppointmentForEdit(appointment)}
                      >
                        {t('common.edit')}
                      </Button>

                      {!appointment.jobcardId && appointment.status !== 'cancelled' && (
                        <Button
                          variant="filled"
                          size="sm"
                          onClick={() => onCreateJobcard(appointment)}
                        >
                          {t('appointments.createJobcard')}
                        </Button>
                      )}
                    </div>

                    {/* Jobcard link */}
                    {appointment.jobcardId && (
                      <div className="text-label-sm pt-1">
                        <Link
                          to={`/jobcards/${appointment.jobcardId}`}
                          className="text-primary interactive hover:underline"
                        >
                          {appointment.jobcardNumber}
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              {/* Show cancelled appointments at the end, muted with strikethrough */}
              {customerAppointments
                .filter((a) => a.status === 'cancelled')
                .map((appointment) => (
                  <div
                    key={appointment.id}
                    className="rounded-md border border-outline-variant bg-surface-high p-3 space-y-2 opacity-60"
                  >
                    {/* Header line */}
                    <div className="text-body-sm text-on-surface line-through">
                      {appointment.time ? formatTime(`2000-01-01T${appointment.time}:00`) : t('appointments.anyTime')}
                      {' · '}
                      {appointment.customerLabel}
                      {appointment.phoneLabel && (
                        <>
                          {' · '}
                          {appointment.phoneLabel}
                        </>
                      )}
                      {appointment.carLabel && (
                        <>
                          {' · '}
                          {appointment.carLabel}
                        </>
                      )}
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center gap-2">
                      <StatusDot
                        tone={APPOINTMENT_TONE[appointment.status]}
                        label={t(`appointments.status.${appointment.status}`, APPOINTMENT_STATUS_LABELS[appointment.status])}
                      />
                    </div>

                    {/* Actions row */}
                    <div className="flex gap-2 flex-wrap pt-1">
                      <Button
                        variant="filled"
                        size="sm"
                        onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'scheduled' })}
                        disabled={setStatusMutation.isPending}
                      >
                        {t('appointments.backToExpected')}
                      </Button>

                      <Button
                        variant="filled"
                        size="sm"
                        onClick={() => onSelectAppointmentForEdit(appointment)}
                      >
                        {t('common.edit')}
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {bookedCars.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-label-lg text-on-surface-variant">{t('calendar.carsBookedIn')}</h3>
            <div className="space-y-1">
              {bookedCars.map((appointment) => (
                <button
                  key={appointment.id}
                  type="button"
                  onClick={() => onSelectAppointment(appointment)}
                  className="w-full flex items-center gap-3 rounded-md px-2 py-2 text-start interactive hover:bg-surface-high"
                >
                  <CarIcon className="w-4 h-4 flex-shrink-0 text-on-surface-variant" />
                  <div className="flex-1 min-w-0">
                    <div className="text-body-md text-on-surface truncate">{appointment.carLabel}</div>
                    {appointment.clientName && (
                      <div className="text-body-sm text-on-surface-variant truncate">{appointment.clientName}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {formatTime(appointment.appointmentAt) && (
                      <span className="text-label-sm text-on-surface-subtle">{formatTime(appointment.appointmentAt)}</span>
                    )}
                    <StatusDot tone={JOBCARD_STATUS_TONE[appointment.status]} label={appointment.number} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {events.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-label-lg text-on-surface-variant">{t('calendar.staffAndEvents')}</h3>
            <div className="space-y-1">
              {events.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => onSelectEvent(event)}
                  className="w-full flex items-center gap-3 rounded-md px-2 py-2 text-start interactive hover:bg-surface-high"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-body-md text-on-surface truncate">
                      {event.workerName ?? t(`calendar.eventTypes.${event.type}`, STAFF_EVENT_TYPE_LABELS[event.type])}
                      {event.type === 'late' && event.arrivalTime && (
                        <span className="text-on-surface-variant"> · {t('calendar.arrivedAt', { time: event.arrivalTime })}</span>
                      )}
                      {event.type === 'overtime' && event.departureTime && (
                        <span className="text-on-surface-variant"> · {t('calendar.leftAt', { time: event.departureTime })}</span>
                      )}
                    </div>
                    {event.note && (
                      <div className="text-body-sm text-on-surface-variant truncate">{event.note}</div>
                    )}
                  </div>
                  <StatusDot tone={EVENT_TONE[event.type]} label={t(`calendar.eventTypes.${event.type}`, STAFF_EVENT_TYPE_LABELS[event.type])} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
